import React, { useEffect } from 'react';
import { useLazyGetCaptchaQuery } from '@/store/api/authApi';
import { useLocation } from 'react-router-dom';

interface CaptchaBoxProps {
  onCaptchaChange: (id: string) => void;
  // Fires once the server tells us whether captcha is required at all — lets
  // the parent stop gating submit on captchaId when it's disabled server-side
  // (data.enabled === false means no captchaId ever arrives).
  onCaptchaEnabledChange?: (enabled: boolean) => void;
  refreshTrigger?: number;
  autoFetch?: boolean;
}

const CaptchaBox: React.FC<CaptchaBoxProps> = ({
  onCaptchaChange,
  onCaptchaEnabledChange,
  refreshTrigger,
  autoFetch = true,
}) => {
  const { pathname } = useLocation();

  const [getCaptcha, { data, isFetching, isError }] = useLazyGetCaptchaQuery();

  // Initial fetch
  useEffect(() => {
    if (autoFetch) {
      getCaptcha();
    }
  }, [autoFetch, getCaptcha]);

  // Refresh captcha before expiry on login page
  useEffect(() => {
    if (pathname !== '/login') return;

    const intervalId = window.setInterval(() => {
      getCaptcha();
    }, 115000);

    return () => window.clearInterval(intervalId);
  }, [pathname, getCaptcha]);

  // Manual refresh
  useEffect(() => {
    if (refreshTrigger === undefined) return;

    getCaptcha();
  }, [refreshTrigger, getCaptcha]);

  // Send captcha ID to parent
  useEffect(() => {
    if (data?.captchaId) {
      onCaptchaChange(data.captchaId);
    }
  }, [data?.captchaId, onCaptchaChange]);

  // Tell parent whether captcha is actually required — when disabled
  // server-side, data.enabled is false and no captchaId ever arrives.
  useEffect(() => {
    if (data?.enabled !== undefined) {
      onCaptchaEnabledChange?.(data.enabled);
    }
  }, [data?.enabled, onCaptchaEnabledChange]);

  return (
    <div className="flex h-[3.125rem] w-[9.375rem] items-center justify-center">
      {isFetching ? (
        <p className="text-xs text-gray-400">Loading...</p>
      ) : data?.image ? (
        <img
          src={data.image}
          alt="Captcha"
          className="h-full w-full object-contain"
          draggable={false}
        />
      ) : (
        <p className="text-xs text-gray-400">
          {isError ? 'Unable to load' : 'Captcha unavailable'}
        </p>
      )}
    </div>
  );
};

export default CaptchaBox;
